import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
} from "@wondermaps/ui";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCommentDots } from "@fortawesome/pro-solid-svg-icons/faCommentDots";
import { faCircleQuestion } from "@fortawesome/pro-solid-svg-icons/faCircleQuestion";
import { faBuilding } from "@fortawesome/pro-solid-svg-icons/faBuilding";
import { faClock } from "@fortawesome/pro-solid-svg-icons/faClock";
import { faCircleCheck } from "@fortawesome/pro-solid-svg-icons/faCircleCheck";
import { faCircle } from "@fortawesome/pro-solid-svg-icons/faCircle";
import { faCircleDot } from "@fortawesome/pro-solid-svg-icons/faCircleDot";
import { faBug } from "@fortawesome/pro-solid-svg-icons/faBug";
import { faLightbulb } from "@fortawesome/pro-solid-svg-icons/faLightbulb";
import { formatDistanceToNow } from "date-fns";
import { SuperadminHeader } from "../components/superadmin/SuperadminHeader";

export const Route = createFileRoute("/superadmin/support")({
  component: SuperadminSupport,
});

const TYPE_CONFIG = {
  support: { label: "Support", icon: faCommentDots, color: "bg-blue-100 text-blue-800" },
  bug: { label: "Bug", icon: faBug, color: "bg-red-100 text-red-800" },
  feature: { label: "Feature", icon: faLightbulb, color: "bg-yellow-100 text-yellow-800" },
  inquiry: { label: "Inquiry", icon: faCircleQuestion, color: "bg-gray-100 text-gray-800" },
} as const;

const STATUS_CONFIG = {
  open: { label: "Open", icon: faCircle, color: "bg-red-100 text-red-800", iconColor: "text-red-600" },
  in_progress: { label: "In Progress", icon: faCircleDot, color: "bg-amber-100 text-amber-800", iconColor: "text-amber-500" },
  closed: { label: "Closed", icon: faCircleCheck, color: "bg-green-100 text-green-800", iconColor: "text-green-600" },
} as const;

type SupportRequest = {
  id: string;
  type: keyof typeof TYPE_CONFIG;
  status: keyof typeof STATUS_CONFIG;
  subject: string;
  description: string;
  metadata: { browser?: string; currentUrl?: string; userAgent?: string } | null;
  createdAt: string;
  updatedAt: string;
  user: { id: string; name: string; email: string };
  organization: { id: string; name: string; slug: string } | null;
};

function SuperadminSupport() {
  const queryClient = useQueryClient();

  // Fetch support requests
  const { data: requests, isLoading } = useQuery({
    queryKey: ["support", "requests"],
    queryFn: async () => {
      const res = await api.support.$get();
      if (!res.ok) throw new Error("Failed to fetch support requests");
      return res.json() as Promise<SupportRequest[]>;
    },
  });

  // Update status mutation
  const updateStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const res = await api.support[":id"].$patch({
        param: { id },
        json: { status: status as "open" | "in_progress" | "closed" },
      });
      if (!res.ok) throw new Error("Failed to update status");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["support", "requests"] });
    },
  });

  const openCount = requests?.filter((r) => r.status === "open").length || 0;
  const inProgressCount = requests?.filter((r) => r.status === "in_progress").length || 0;

  return (
    <div className="flex h-screen flex-col bg-background">
      <SuperadminHeader
        title="Support Requests"
        subtitle={`${openCount} open, ${inProgressCount} in progress`}
      />

      {/* Content */}
      <main className="flex-1 overflow-auto p-6">
        <div className="container mx-auto max-w-4xl space-y-4">
          {isLoading ? (
            <>
              {[1, 2, 3].map((i) => (
                <Card key={i}>
                  <CardHeader>
                    <Skeleton className="h-5 w-48" />
                    <Skeleton className="h-4 w-32 mt-2" />
                  </CardHeader>
                  <CardContent>
                    <Skeleton className="h-16 w-full" />
                  </CardContent>
                </Card>
              ))}
            </>
          ) : requests?.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <FontAwesomeIcon icon={faCommentDots} className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No support requests yet.</p>
              </CardContent>
            </Card>
          ) : (
            requests?.map((request) => {
              const typeConfig = TYPE_CONFIG[request.type];
              const TypeIcon = typeConfig.icon;

              return (
                <Card key={request.id}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between gap-4">
                      <div className="space-y-1 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="outline" className={typeConfig.color}>
                            <FontAwesomeIcon icon={TypeIcon} className="h-3 w-3 mr-1" />
                            {typeConfig.label}
                          </Badge>
                          <CardTitle className="text-base">{request.subject}</CardTitle>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span>{request.user.email}</span>
                          {request.organization && (
                            <span className="flex items-center gap-1">
                              <FontAwesomeIcon icon={faBuilding} className="h-3 w-3" />
                              {request.organization.name}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <FontAwesomeIcon icon={faClock} className="h-3 w-3" />
                            {formatDistanceToNow(new Date(request.createdAt), { addSuffix: true })}
                          </span>
                        </div>
                      </div>

                      <Select
                        value={request.status}
                        onValueChange={(value) =>
                          updateStatus.mutate({ id: request.id, status: value })
                        }
                      >
                        <SelectTrigger className="w-36">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {(Object.entries(STATUS_CONFIG) as [keyof typeof STATUS_CONFIG, typeof STATUS_CONFIG[keyof typeof STATUS_CONFIG]][]).map(([value, cfg]) => (
                            <SelectItem key={value} value={value}>
                              <span className="flex items-center gap-2">
                                <FontAwesomeIcon icon={cfg.icon} className={`h-3 w-3 ${cfg.iconColor}`} />
                                {cfg.label}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm whitespace-pre-wrap">{request.description}</p>
                    {request.metadata && (
                      <div className="mt-4 p-3 bg-muted rounded-md text-xs font-mono space-y-1">
                        {request.metadata.currentUrl && (
                          <p>
                            <span className="text-muted-foreground">URL:</span>{" "}
                            {request.metadata.currentUrl}
                          </p>
                        )}
                        {request.metadata.browser && (
                          <p>
                            <span className="text-muted-foreground">Browser:</span>{" "}
                            {request.metadata.browser}
                          </p>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </main>
    </div>
  );
}
